import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { ChartData } from 'chart.js';
import { forkJoin } from 'rxjs';
import { ViveProgress } from 'src/app/classes/models';
import { WidgetPanelChart } from 'src/app/interfaces/widget-panel-chart';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { SharedDataService } from 'src/app/services/shared-data.service';

@Component({
  selector: 'app-resolving-vendors-widget',
  templateUrl: './resolving-vendors-widget.component.html',
  styleUrls: ['./resolving-vendors-widget.component.less'],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ResolvingVendorsWidgetComponent {
  private _cdr = inject(ChangeDetectorRef);
  widgetPanelChart: WidgetPanelChart[] = [];
  title: string = 'Resolving Vendors';
  showMoreTrades: boolean = false;
  countMap: { type: ViveProgress['type']; count: number; query: {} }[] = [];

  constructor(
    private CompanyVendorService: CompanyVendorService,
    private SharedDataService: SharedDataService,
    private Router: Router
  ) {
    this.SharedDataService.listenForEvent<
      ViveProgress['type'] | null | undefined
    >().subscribe({
      next: ({ eventName, data }) => {
        if (
          eventName === 'viveProgressTypeLabelSelected' &&
          this.isViveProgressType(data)
        ) {
          this.navigateToRunViveGrid(data);
        }
      }
    });

    forkJoin([
      this.CompanyVendorService.getCompanyVendorCount(
        this.getCompanyVendorQuery('Vendor Info')
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        this.getCompanyVendorQuery('Owner Info')
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        this.getCompanyVendorQuery('Trades & Licenses')
      ),
      this.CompanyVendorService.getCompanyVendorCount(
        this.getCompanyVendorQuery('Insurance')
      )
    ]).subscribe({
      next: ([vendorInfo, ownerInfo, trades, insurance]) => {
        this.countMap.push({
          type: 'Vendor Info',
          count: vendorInfo,
          query: this.getCompanyVendorQuery('Vendor Info')
        });
        this.countMap.push({
          type: 'Owner Info',
          count: ownerInfo,
          query: this.getCompanyVendorQuery('Owner Info')
        });
        this.countMap.push({
          type: 'Trades & Licenses',
          count: trades,
          query: this.getCompanyVendorQuery('Trades & Licenses')
        });
        this.countMap.push({
          type: 'Insurance',
          count: insurance,
          query: this.getCompanyVendorQuery('Insurance')
        });

        this.formatData(this.countMap);
        this._cdr.markForCheck();
      }
    });
  }

  isViveProgressType(string: any): string is ViveProgress['type'] {
    const viveProgressTypes: ViveProgress['type'][] = [
      'Insurance',
      'Trades & Licenses',
      'Vendor Info',
      'Owner Info',
      'Final Review'
    ];

    return viveProgressTypes.includes(string);
  }

  private navigateToRunViveGrid(viveProgressType: ViveProgress['type']) {
    this.Router.navigate(['admin', 'run-vive'], {
      state: { query: this.getCompanyVendorQuery(viveProgressType) }
    });
  }

  private getCompanyVendorQuery(type: ViveProgress['type']) {
    const query = {
      $and: JSON.stringify([
        {
          viveStatus: {
            $notIn: [
              'Inactive',
              'Unregistered',
              'Registering',
              'Awaiting Payment'
            ]
          }
        },
        {
          $or: [
            { viveStatus: { $ne: 'Suspended' } },
            {
              viveStatus: 'Suspended',
              viveSubStatus: {
                $notIn: [
                  'No Renewal',
                  'Cancelled Insurance',
                  'Expired Insurance'
                ]
              }
            }
          ]
        }
      ]),
      ViveProgress: JSON.stringify({
        options: { required: true },
        status: 'Resolving',
        type
      })
    };

    return query;
  }

  formatData(data: { type: ViveProgress['type']; count: number }[]) {
    const chartData: ChartData = {
      labels: data.map((data) => data.type),
      datasets: [
        {
          data: data.map((data) => data.count)
        }
      ]
    };

    this.widgetPanelChart.push({
      title: this.title,
      data: {
        chartTitle: this.title,
        chartType: 'bar',
        chartData: chartData,
        chartOptions: {
          indexAxis: 'y',
          scales: {
            x: {
              display: false,
              border: {
                display: false
              },
              grid: {
                display: false
              }
            },
            y: {
              display: true,
              border: {
                display: false
              },
              grid: {
                display: false
              }
            }
          },
          layout: {
            padding: {
              right: 25
            }
          },
          plugins: {
            legend: {
              display: false
            }
          }
        },
        chartStyle: {
          padding: '1em 0'
        },
        displayChartTitle: false
      }
    });
  }
}
